import React, { useCallback } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import { EmptyArray } from 'lib/array/arrayUtils'
import { isRoomsInvalid } from 'lib/search/searchUtils'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import TextButton from 'LuxKit/Button/TextButton'
import SoftOfferPageWalledGarden from 'components/Common/SoftOfferPageWalledGarden'
import OfferPageStickyFooter from '../../OfferPageStickyFooter'
import { hasSelectedRooms, preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'

const StyledOfferPageStickyFooter = styled(OfferPageStickyFooter)`
  padding: ${rem(12)} ${rem(20)} ${rem(20)} ${rem(20)};
`
interface Props {
  offer: App.BedbankOffer
  rooms?: Array<App.RoomOccupants>
  checkInDate?: string
}

function BedbankOfferPageStickyFooterMobile(props: Props) {
  const {
    rooms = EmptyArray,
    checkInDate,
    offer,
  } = props

  const intl = useIntl()
  const offerPageDispatch = useOfferPageDispatch()

  const invalidRoomSelection = !hasSelectedRooms(rooms) || isRoomsInvalid(rooms)
  const canViewOptions = !!checkInDate && !invalidRoomSelection
  const checkAvailabilityLabel = intl.formatMessage({
    id: 'auljViK0wDMuPXeoTXVY',
    defaultMessage: 'Check availability',
    description: 'Button text when no dates selected on bedbank offer page sticky footer',
  })
  const viewRoomsLabel = intl.formatMessage({
    id: 'qlVLvk3lCYQ82TGVM1GM',
    defaultMessage: 'View rooms',
    description: 'Button text to view available rooms on bedbank offer page sticky footer',
  })
  const viewCountRoomsLabel = intl.formatMessage(
    {
      id: 'y4kquPY9Rrx9RBniJYZm',
      defaultMessage: 'View {count, plural, one {# room} other {# rooms}}',
      description: 'Button text to view selected rooms with count on bedbank offer page sticky footer',
    },
    { count: rooms.length },
  )
  let ctaLabel: string
  if (!canViewOptions) {
    ctaLabel = checkAvailabilityLabel
  } else if (rooms.length > 1) {
    ctaLabel = viewCountRoomsLabel
  } else {
    ctaLabel = viewRoomsLabel
  }

  const onClickAvailability = useCallback(() => {
    if (invalidRoomSelection) {
      return offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'capacity' })
    }

    if (!checkInDate) {
      return offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
    }

    // Reset previous room selected
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: undefined })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'packageSelection' })
  }, [checkInDate, invalidRoomSelection, offerPageDispatch])

  return (
    <StyledOfferPageStickyFooter
      visible
      callToActionDisabled
      offer={offer}
    >
      <SoftOfferPageWalledGarden>
        <TextButton fit="full-width" size="large" kind="primary" onClick={onClickAvailability}>
          {ctaLabel}
        </TextButton>
      </SoftOfferPageWalledGarden>
    </StyledOfferPageStickyFooter>
  )
}

export default withOfferPageAndReduxState((state) => ({
  offer: state.offer,
  rooms: state.rooms,
  checkInDate: state.checkInDate,
  open: !!state.activeFilter,
}), BedbankOfferPageStickyFooterMobile, preventRenderWithOpenFilter)
